import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, memo, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { useCommentsRoot } from 'components/Comments/Root';

import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import Body from './Body';
import Header from './Header';

import COMMENT from 'constants/comment';

const Editing = dynamic(() => import('../Editing'), { ssr: false });

const CommentItem = forwardRef(
  (
    {
      canBeRecommendedAsReview,
      children,
      commentDate,
      commentFixed = false,
      commentId,
      commentLikes,
      commentStatusName,
      commentText,
      hasReply = false,
      isDisliked = false,
      isEdited,
      isLiked = false,
      isReply = false,
      isReview = false,
      isShowingReplies = false,
      parentId,
      userId,
      userLevel,
      userName,
      userPhoto,
      userSpecialtyName = null,
      userTypeName,
      userUsername,
      onToggleReplies = noop,
    },
    ref
  ) => {
    const [isEditing, setEditing] = useState(false);
    const { gaEventCategory } = useCommentsRoot();
    const { loadingLoggedInUser, LoggedInUser } = useUser();

    /**
     * This is a control made for content blocked comment statuses (USELESS,
     * PARTIALLY_REMOVED) that can be seen if the user wants to do so.
     */
    const [
      isContentBlockedViewableCommentHidden,
      setContentBlockedViewableCommentHidden,
    ] = useState(true);

    const isContentBlocked = useMemo(
      () =>
        commentStatusName === COMMENT.STATUSES.REMOVED_BY_ADMIN ||
        commentStatusName === COMMENT.STATUSES.REMOVED_BY_USER ||
        (LoggedInUser?.userId !== userId &&
          commentStatusName === COMMENT.STATUSES.REMOVED_FOR_ALL) ||
        (commentStatusName === COMMENT.STATUSES.USELESS &&
          isContentBlockedViewableCommentHidden &&
          LoggedInUser?.userId !== userId) ||
        (commentStatusName === COMMENT.STATUSES.PARTIALLY_REMOVED &&
          isContentBlockedViewableCommentHidden &&
          LoggedInUser?.userId !== userId),
      [
        commentStatusName,
        loadingLoggedInUser,
        isContentBlockedViewableCommentHidden,
      ]
    );

    const onEditCommentClick = () => {
      setEditing(true);
    };

    const onStopEditing = () => {
      setEditing(false);
    };

    if (loadingLoggedInUser) {
      return null;
    }

    return (
      <div
        className={twJoin(
          'rounded-4 bg-neutral-high-200 py-1 dark:bg-neutral-low-400',
          !isReply && 'flex w-full flex-col px-4'
        )}
      >
        <div
          ref={ref}
          className={twJoin(
            'relative flex w-full',
            isReply && 'flex-col pl-10 md:flex-row'
          )}
        >
          <div className={twJoin('w-full', isReply && 'py-2')}>
            <Header
              canBeRecommendedAsReview={canBeRecommendedAsReview}
              commentDate={commentDate}
              commentFixed={commentFixed}
              commentId={commentId}
              gaEventCategory={gaEventCategory}
              isContentBlocked={isContentBlocked}
              isEdited={isEdited}
              isReply={isReply}
              isReview={isReview}
              userId={userId}
              userLevel={userLevel}
              userName={userName}
              userPhoto={userPhoto}
              userSpecialtyName={userSpecialtyName}
              userTypeName={userTypeName}
              userUsername={userUsername}
              onEditCommentClick={onEditCommentClick}
            />
            {isEditing ? (
              <Editing
                commentId={commentId}
                commentText={commentText}
                gaEventCategory={gaEventCategory}
                parentId={parentId}
                onStopEditing={onStopEditing}
              />
            ) : (
              <Body
                commentFixed={commentFixed}
                commentId={commentId}
                commentLikes={commentLikes}
                commentStatusName={commentStatusName}
                commentText={commentText}
                hasReply={hasReply}
                isContentBlocked={isContentBlocked}
                isDisliked={isDisliked}
                isEdited={isEdited}
                isLiked={isLiked}
                isReply={isReply}
                isShowingReplies={isShowingReplies}
                onShowCommentLinkClick={() => {
                  setContentBlockedViewableCommentHidden(false);
                }}
                onToggleReplies={onToggleReplies}
              />
            )}
          </div>
        </div>
        {children}
      </div>
    );
  }
);

CommentItem.displayName = 'CommentItem';

CommentItem.propTypes = {
  canBeRecommendedAsReview: PropTypes.bool,
  children: PropTypes.node,
  commentDate: PropTypes.string.isRequired,
  commentFixed: PropTypes.bool,
  commentId: PropTypes.number.isRequired,
  commentLikes: PropTypes.number,
  commentStatusName: PropTypes.string.isRequired,
  /**
   * Should be required but there are edge cases where it could be null.
   * There were bugs where users could send an empty comment
   */
  commentText: PropTypes.string,
  hasReply: PropTypes.bool,
  isDisliked: PropTypes.bool,
  isEdited: PropTypes.bool.isRequired,
  isLiked: PropTypes.bool,
  isReply: PropTypes.bool,
  isReview: PropTypes.bool,
  isShowingReplies: PropTypes.bool,
  parentId: PropTypes.number,
  userId: PropTypes.number.isRequired,
  userLevel: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userTypeName: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  onToggleReplies: PropTypes.func,
};

export default memo(CommentItem);
